import React, { useState } from 'react';
import { Box, TextField } from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

// x-y, a non-negative range
const RANGE_PATTERN = /^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/;

export function validateWeeksBehind(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.toLowerCase() === 'n/a') return null;
  if (trimmed !== '' && Number.isFinite(Number(trimmed)) && Number(trimmed) >= 0) return null;
  if (RANGE_PATTERN.test(trimmed)) return null;
  return 'Preferred input: a non-negative number (e.g. 3), "n/a", or a range (e.g. 2-4)';
}

/**
 * WeeksBehindToReport is always a non-negative magnitude: zero = on time, greater than
 * zero = behind schedule. It never represents "ahead of schedule" - that is determined
 * separately from the CDCC Estimated vs. Revised/Original completion dates.
 */
export function getWeeksBehindDirection(raw: string): 'behind' | 'onTime' {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.toLowerCase() === 'n/a') return 'onTime';
  const rangeMatch = trimmed.match(RANGE_PATTERN);
  const num = rangeMatch ? parseFloat(rangeMatch[1]) : parseFloat(trimmed);
  if (!Number.isFinite(num)) return 'onTime';
  return num > 0 ? 'behind' : 'onTime';
}

/** Display form of the weeks-behind value, for use in "approximately X weeks behind" style text. */
export function getWeeksBehindMagnitudeDisplay(raw: string): string {
  const trimmed = raw.trim();
  const rangeMatch = trimmed.match(RANGE_PATTERN);
  if (rangeMatch) {
    return `${rangeMatch[1]}-${rangeMatch[2]}`;
  }
  const num = parseFloat(trimmed);
  return Number.isFinite(num) ? `${Math.abs(num)}` : trimmed;
}

interface WeeksBehindFieldRendererProps {
  value?: string;
  addToActionItems?: boolean;
  onChange: (value: string, addToActionItems: boolean) => void;
  label?: string;
  showBookmark?: boolean;
  disabled?: boolean;
  /** Highlights the field in red to prompt the inspector to review (does not block saving). */
  isSameAsLastMonth?: boolean;
}

export const WeeksBehindFieldRenderer: React.FC<WeeksBehindFieldRendererProps> = ({
  value = '',
  addToActionItems = false,
  onChange,
  label,
  showBookmark = true,
  disabled = false,
  isSameAsLastMonth = false,
}) => {
  const [touched, setTouched] = useState(false);
  const errorMessage = touched ? validateWeeksBehind(value) : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <TextField
        fullWidth
        type='text'
        label={label}
        value={value}
        onChange={e => onChange(e.target.value, addToActionItems)}
        onBlur={() => setTouched(true)}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        helperText={
          errorMessage ? (
            <span style={{ color: '#ed6c02' }}>{errorMessage}</span>
          ) : isSameAsLastMonth ? (
            <span style={{ color: '#d32f2f' }}>Please confirm, currently same as last month</span>
          ) : undefined
        }
        sx={
          isSameAsLastMonth
            ? {
                '& .MuiOutlinedInput-root fieldset': { borderColor: '#d32f2f' },
                '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#d32f2f' },
                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#d32f2f' },
              }
            : undefined
        }
      />
      {showBookmark ? (
        <Box sx={{ cursor: 'pointer', mt: '9px', flexShrink: 0 }} onClick={() => onChange(value, !addToActionItems)}>
          {addToActionItems ? <BookmarkIcon sx={{ color: 'primary.main' }} /> : <BookmarkBorderIcon sx={{ color: 'action.disabled' }} />}
        </Box>
      ) : (
        <Box sx={{ width: 24, flexShrink: 0 }} />
      )}
    </Box>
  );
};
